import { Box } from "@mui/material";
import ConfirmationDialog from "apps/web/src/componentLibrary/ConfirmationDialog/ConfirmationDialog";
import { useBrowserBackAndRefreshWarning } from "apps/web/src/hooks/useBrowserBackAndRefreshWarning";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import { useRevisionGatedAiFlow, type RecipeAiFlow } from "apps/web/src/hooks/useRevisionGatedAiFlow";
import { RecipePrintDialog } from "apps/web/src/views/Recipes/components/Printing/RecipePrintDialog";
import React, { useCallback, useReducer, useRef, useState } from "react";
import { useEnvironmentType } from "../../../contexts/useEnvType";
import { EditRecipeFullInput, EditRecipePartialInput, GptRecipeFragment, RecipeFullFragment } from "../../../types";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import DiscardChangesDialog from "../../universal/DiscardChangesDialog";
import RecipeForm from "../Form/RecipeForm";
import { canSaveRecipeFormTooltips, newRecipeFormStateFromGPTRecipe, RecipeFormState } from "../Form/utils";
import { GenerateWithGPTDialog } from "../GPT/Generator/GenerateWithGPTDialog";
import { openTrackedRecipeAiFlow } from "../GPT/recipeAiFlow";
import { TranscribeWithGPTDialog } from "../GPT/Transcriber/TranscribeWithGPTDialog";
import { TranscribeError, getUserFriendlyMessage } from "../GPT/Transcriber/utils";
import { EditRecipeHeader } from "../Header/EditRecipeHeader";
import { RecipeScaleDialog } from "../Scale/RecipeScaleDialog";
import RecipeScaleSuccessAlert from "../Scale/RecipeScaleSuccessAlert";
import { RevisionRecipeDialog } from "./RevisionRecipeDialog";
import { editRecipeReducer, editRecipeStateToInput, recipeToEditState } from "./utils";

interface RecipeEditContentProps {
  onClose: () => void;
  onEditFull: (input: EditRecipeFullInput) => void;
  onEditPartial: (input: EditRecipePartialInput) => void;
  recipe: RecipeFullFragment;
  onDuplicate: () => void;
  forStaff?: boolean;
  readonly?: boolean;
  onScaleAndCreateNewRecipe: (state: RecipeFormState) => void;
}

export const RecipeEditContent = ({
  onClose,
  onEditFull,
  onEditPartial,
  recipe,
  onDuplicate,
  forStaff,
  readonly,
  onScaleAndCreateNewRecipe,
}: RecipeEditContentProps) => {
  const [state, dispatch] = useReducer(editRecipeReducer, recipeToEditState(recipe));
  const { setBrowserBackAndRefreshWarningEnabled } = useBrowserBackAndRefreshWarning();
  const canSaveTooltips = canSaveRecipeFormTooltips(state.form);
  const [showScaleSuccessMessage, setShowScaleSuccessMessage] = useState(false);
  const [openScaleRecipe, setOpenScaleRecipe] = useState(false);
  const [openRecipeUnsavedChangesDialog, setOpenRecipeUnsavedChangesDialog] = useState(false);
  const [pendingFullSaveInput, setPendingFullSaveInput] = useState<EditRecipeFullInput | null>(null);
  const initialFormState = useRef<RecipeFormState>(state.form);

  const { isCommercial } = useEnvironmentType();
  const { isGetRecipeFromUrlEnabled, useBedrock } = useNutritionFlags();
  const { setMessage } = useSnackbar();
  const showAIRecipes = !readonly && !forStaff && (isCommercial || useBedrock);
  const [transcriberError, setTranscriberError] = useState<TranscribeError | null>(null);
  const [showGenerateRecipeModal, setShowGenerateRecipeModal] = useState(false);
  const [showTranscribePhotoModal, setShowTranscribePhotoModal] = useState(false);
  const [showImportLinkModal, setShowImportLinkModal] = useState(false);

  const submitInput = (maybeInput: ReturnType<typeof editRecipeStateToInput>) => {
    if (maybeInput?.type === "full") {
      onEditFull(maybeInput.input);
    } else if (maybeInput?.type === "partial") {
      onEditPartial(maybeInput.input);
    }
  };

  const handleSave = () => {
    if (readonly) {
      return;
    }
    const maybeInput = editRecipeStateToInput(state);
    if (maybeInput?.type === "full" && recipe.isUsedByMenuItem) {
      setPendingFullSaveInput(maybeInput.input);
      return;
    }
    submitInput(maybeInput);
  };

  const handleConfirmApplyChanges = () => {
    if (pendingFullSaveInput) {
      onEditFull(pendingFullSaveInput);
      setPendingFullSaveInput(null);
    }
  };

  const onGenerateError = (error: unknown) => {
    const userFriendlyMessage = getUserFriendlyMessage(error);
    setMessage("error", userFriendlyMessage);
  };

  const onGPTRecipeComplete = (gptRecipe: GptRecipeFragment, closeModal: () => void) => {
    const gptState = newRecipeFormStateFromGPTRecipe(gptRecipe);
    dispatch({
      type: "REPLACE_STATE",
      payload: {
        ...gptState,
        id: state.form.id,
        isShared: state.form.isShared,
        serving: {
          ...gptState.serving,
          id: state.form.serving.id,
        },
      },
    });
    closeModal();
    setShowTranscribePhotoModal(false);
    setShowImportLinkModal(false);
  };

  const openAiFlow = useCallback((flow: RecipeAiFlow) => {
    openTrackedRecipeAiFlow(flow, "Recipe", {
      generate: () => setShowGenerateRecipeModal(true),
      photo: () => setShowTranscribePhotoModal(true),
      link: () => setShowImportLinkModal(true),
    });
  }, []);

  const makeRevision = useCallback(() => {
    dispatch({ type: "MAKE_REVISION" });
  }, []);

  const { isAwaitingRevisionForAi, requestAiFlow, cancelPendingAiFlow, confirmRevisionAndOpenAi } = useRevisionGatedAiFlow({
    isRevision: state.isRevision,
    onMakeRevision: makeRevision,
    onOpenFlow: openAiFlow,
  });

  const showRevisionDialog = state.pendingRevisionAction !== null || isAwaitingRevisionForAi;

  const handleRevisionClose = () => {
    if (isAwaitingRevisionForAi) {
      cancelPendingAiFlow();
      return;
    }
    dispatch({ type: "CANCEL_REVISION" });
  };

  React.useEffect(() => {
    setBrowserBackAndRefreshWarningEnabled(state.form.edited);
  }, [state.form.edited, setBrowserBackAndRefreshWarningEnabled]);

  const [discardChangesOpen, setDiscardChangesOpen] = useState(false);

  const handleClose = () => {
    if (state.form.edited) {
      setDiscardChangesOpen(true);
    } else {
      onClose();
    }
  };

  const [printOpen, setPrintOpen] = useState(false);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <EditRecipeHeader
        readonly={readonly}
        handleClose={handleClose}
        setPrintOpen={setPrintOpen}
        handleDuplicate={onDuplicate}
        forStaff={forStaff}
        isShared={state.form.isShared}
        onChange={() =>
          dispatch({
            type: "CHANGE_IS_SHARED",
            value: !state.form.isShared,
          })
        }
        handleSave={handleSave}
        canSaveTooltips={canSaveTooltips}
      />
      <Box>
        <RecipeScaleSuccessAlert open={showScaleSuccessMessage} onClose={() => setShowScaleSuccessMessage(false)} />
        <RecipeForm
          state={state.form}
          dispatch={dispatch}
          sx={{ flexGrow: 1 }}
          disabled={readonly}
          onAiGenerateClicked={showAIRecipes ? () => requestAiFlow("generate") : null}
          onAiTranscribeClicked={showAIRecipes ? () => requestAiFlow("photo") : undefined}
          onImportFromLinkClicked={showAIRecipes && isGetRecipeFromUrlEnabled ? () => requestAiFlow("link") : undefined}
          openScaleRecipe={() => {
            if (state.form.edited) {
              setOpenRecipeUnsavedChangesDialog(true);
            } else {
              setOpenScaleRecipe(true);
            }
          }}
          forStaff={forStaff}
        />
      </Box>
      {discardChangesOpen && (
        <DiscardChangesDialog
          open={discardChangesOpen}
          onClose={() => setDiscardChangesOpen(false)}
          onDiscard={() => {
            setBrowserBackAndRefreshWarningEnabled(false);
            onClose();
          }}
        />
      )}
      <RecipeScaleDialog
        state={state.form}
        initialFormState={initialFormState.current}
        dispatch={dispatch}
        openScaleChangeDialog={openScaleRecipe}
        setOpenScaleChangeDialog={setOpenScaleRecipe}
        openRecipeUnsavedChangesDialog={openRecipeUnsavedChangesDialog}
        setOpenRecipeUnsavedChangesDialog={setOpenRecipeUnsavedChangesDialog}
        onSuccess={setShowScaleSuccessMessage}
        openScaleRecipe={value => setOpenScaleRecipe(value)}
        onScaleAndCreateNewRecipe={onScaleAndCreateNewRecipe}
      />
      {showRevisionDialog && (
        <RevisionRecipeDialog open={showRevisionDialog} onClose={handleRevisionClose} onSubmit={confirmRevisionAndOpenAi} />
      )}

      <ConfirmationDialog
        open={pendingFullSaveInput !== null}
        title="Apply Changes to Menu Items"
        message="Changes to this recipe will apply to the associated menu item(s) from today going forward. Any previous menus with this menu item will not be changed."
        cancelLabel="Cancel"
        confirmLabel="Confirm"
        onCancel={() => setPendingFullSaveInput(null)}
        onConfirm={handleConfirmApplyChanges}
      />

      {showGenerateRecipeModal && (
        <GenerateWithGPTDialog
          open={showGenerateRecipeModal}
          onClose={() => setShowGenerateRecipeModal(false)}
          onGenerateComplete={onGPTRecipeComplete}
          onError={onGenerateError}
          initialName={state.form.name}
        />
      )}
      {showTranscribePhotoModal && (
        <TranscribeWithGPTDialog
          open={showTranscribePhotoModal}
          onClose={() => setShowTranscribePhotoModal(false)}
          onTranscribeComplete={onGPTRecipeComplete}
          transcriberError={transcriberError}
          setTranscriberError={setTranscriberError}
          onError={onGenerateError}
          mode="photo"
        />
      )}
      {showImportLinkModal && (
        <TranscribeWithGPTDialog
          open={showImportLinkModal}
          onClose={() => setShowImportLinkModal(false)}
          onTranscribeComplete={onGPTRecipeComplete}
          transcriberError={transcriberError}
          setTranscriberError={setTranscriberError}
          onError={onGenerateError}
          mode="link"
        />
      )}

      {printOpen && <RecipePrintDialog recipeId={state.id} onClose={() => setPrintOpen(false)} />}
    </Box>
  );
};
